import React from 'react';
import { useQuery } from '@apollo/client';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Star } from 'lucide-react';
import Button from '../components/ui/Button';
import UserAvatar from '../components/ui/UserAvatar';
import { GET_TOOL_BY_ID } from '../graphql/queries/getTool';

const ToolReviews: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data, loading, error } = useQuery(GET_TOOL_BY_ID, {
    variables: {
      id,
      availabilityStartDate: new Date().toISOString().split('T')[0],
    },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const tool = data?.tool;

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('da-DK', options);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <p className="text-gray-600">Indlæser bedømmelser...</p>
      </div>
    );
  }

  if (error || !tool) {
    return (
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <p className="text-red-600">Kunne ikke indlæse bedømmelser.</p>
        <div className="mt-4">
          <Link to={id ? `/tool/${id}` : '/browse'}>
            <Button variant="outline" icon={<ArrowLeft className="h-4 w-4 mr-2" />}>
              Tilbage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const reviews = tool.reviews || [];
  const ratingAverage = tool.ratingAverage ? tool.ratingAverage.toFixed(1) : '0';

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex items-center text-sm text-gray-500 mb-4">
        <Link to="/" className="hover:text-gray-700">Forside</Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <Link to="/browse" className="hover:text-gray-700">Find værktøj</Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <Link to={`/tool/${tool.id}`} className="hover:text-gray-700">{tool.name}</Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-gray-900">Bedømmelser</span>
      </div>

      <div className="mb-6">
        <Link to={`/tool/${tool.id}`}>
          <Button variant="outline" icon={<ArrowLeft className="h-4 w-4 mr-2" />}>
              Tilbage til værktøj
          </Button>
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-center gap-4 justify-between mb-6 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Bedømmelser af {tool.name}</h1>
          <div className="flex items-center">
            <span className="text-yellow-500 flex mr-2">
              {[1, 2, 3, 4, 5].map((rating) => (
                <Star
                  key={rating}
                  className={`h-5 w-5 ${rating <= Math.floor(tool.ratingAverage || 0) ? 'fill-current' : ''}`}
                />
              ))}
            </span>
            <span className="font-medium text-gray-900">{ratingAverage}</span>
            <span className="ml-2 text-gray-500">({tool.reviewCount || 0} bedømmelser)</span>
          </div>
        </div>

        {reviews.length === 0 ? (
          <p className="text-gray-600">Der er endnu ingen bedømmelser af dette værktøj.</p>
        ) : (
          <div className="space-y-6">
            {reviews.map((review: any) => (
              <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                <div className="flex items-center mb-2">
                  <UserAvatar user={review.createdByUser} />
                  <div className="ml-5">
                    <div className="font-medium text-gray-900">{review.createdByUser?.name}</div>
                    <div className="text-sm text-gray-500">{formatDate(review.createdAt)}</div>
                  </div>
                </div>
                <div className="flex text-yellow-500 my-1">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Star
                      key={rating}
                      className={`h-4 w-4 ${rating <= review.rating ? 'fill-current' : ''}`}
                    />
                  ))}
                </div>
                <p className="text-gray-700 mt-2">{review.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolReviews;
